import React, { forwardRef, useImperativeHandle, Ref } from 'react';
import {
  SelectionAdapterHook,
  EntityName,
} from 'src/components/DataTable/types/DataTableSelection';
import { DataTableSelectionRef } from 'src/components/DataTable/types/DataTableRef';
import { useDataTableSelection } from './hooks/useDataTableSelection';
import { DataTableSelectionContext } from './DataTableSelectionContext';

export interface DataTableSelectionProviderProps<TData = unknown> {
  adapter: SelectionAdapterHook;
  idKey?: keyof TData;
  children: React.ReactNode;
  entityName?: EntityName;
  primaryActionName?: string;
  secondaryActionName?: string;
  hideSelectionMessage?: boolean;
}

function DataTableSelectionProviderInner<TData = unknown>(
  {
    adapter,
    idKey = 'id' as keyof TData,
    children,
    entityName,
    primaryActionName,
    secondaryActionName,
    hideSelectionMessage = false,
  }: DataTableSelectionProviderProps<TData>,
  ref: Ref<DataTableSelectionRef>
): JSX.Element {
  const contextValue = useDataTableSelection<TData>(adapter, idKey, {
    entityName,
    primaryActionName,
    secondaryActionName,
    hideSelectionMessage,
  });

  useImperativeHandle(
    ref,
    () => ({
      getCurrentSelections: () => contextValue.currentSelections,
      getInitialSelections: () => contextValue.initialSelections,
      isSelected: contextValue.isSelected,
      toggleSelection: contextValue.toggleSelection,
      toggleAll: contextValue.toggleAll,
      resetSelections: contextValue.resetSelections,
      saveSelections: contextValue.saveSelections,
      isDirty: () => contextValue.isDirty,
    }),
    [
      contextValue.currentSelections,
      contextValue.initialSelections,
      contextValue.isSelected,
      contextValue.toggleSelection,
      contextValue.toggleAll,
      contextValue.resetSelections,
      contextValue.saveSelections,
      contextValue.isDirty,
    ]
  );

  return (
    <DataTableSelectionContext.Provider value={contextValue}>
      {children}
    </DataTableSelectionContext.Provider>
  );
}

export const DataTableSelectionProvider = forwardRef(
  DataTableSelectionProviderInner
) as <TData = unknown>(
  props: DataTableSelectionProviderProps<TData> & {
    ref?: Ref<DataTableSelectionRef>;
  }
) => JSX.Element;
